import type { BlockPromptContext } from './types.ts';
import { buildProgressionClause, buildExampleClause, buildCrossMonthClause } from './types.ts';
import { buildPersonalizationClause } from './personalization-helpers.ts';
import { SOURCE_LANGUAGES, TARGET_LANGUAGES } from '../../constants/languages.ts';

function buildPreviousVerbsClause(ctx: BlockPromptContext): string {
  if (!ctx.previousMonthsVerbs || ctx.previousMonthsVerbs.length === 0) return '';
  return `

## Verbs Covered in Previous Months
${ctx.previousMonthsVerbs.map((v) => `- ${v}`).join('\n')}

**Rule:** Pick different verbs than those listed above. The learner has already practiced these in previous workbooks.`;
}

export function buildConjugationSpotlightPrompt(ctx: BlockPromptContext): string {
  const source = SOURCE_LANGUAGES.find((l) => l.code === ctx.profile.native_language);
  const target = TARGET_LANGUAGES.find((l) => l.code === ctx.profile.target_language);

  return `Generate a **Conjugation Spotlight** exercise block for a ${ctx.profile.level.toUpperCase()} ${target?.name} learner whose native language is ${source?.name}.

## Theme
Workbook theme: "${ctx.workbookTheme}"
Sub-theme for this block: "${ctx.subTheme}"

## Block Position
This is block ${ctx.blockOrder} of ${ctx.totalBlocks}.
${buildPersonalizationClause(ctx.profile, ctx.blockOrder, ctx.isPreview)}

## Requirements

1. Select **2-3 verbs** relevant to "${ctx.subTheme}" that are important for a ${ctx.profile.level.toUpperCase()} learner.
2. Include at least 1 irregular verb if level is B1 or above.
3. For each verb provide:
   - \`infinitive\`: The base form in ${target?.name}
   - \`translation\`: In ${source?.name}
   - \`tense\`: Choose a tense appropriate for the level (A1-A2: present only; B1+: can include past/conditional)
   - \`conjugations\`: Full conjugation table as \`Record<string, string>\` with at least 5 person forms (yo, tú, él/ella/usted, nosotros, ellos/ustedes)
   - \`isIrregular\`: boolean
4. Generate **5-6 fill-in exercises** using these verbs:
   - \`sentence\`: A sentence in ${target?.name} with "___" replacing the conjugated form, plus the infinitive in parentheses as a hint: "Yo ___ (tener) dolor."
   - \`hint\`: The sentence meaning in ${source?.name}
   - \`answer\`: The correct conjugated form
   - \`verb\`: The infinitive this exercise tests (must match a verb in the verbs array)
5. Distribute exercises across all verbs.

## Output Format

Return a single JSON object matching:

\`\`\`typescript
interface ConjugationSpotlightBlock {
  id: string;
  type: "conjugation_spotlight";
  order: ${ctx.blockOrder};
  title: string;
  titleNative: string;
  theme: "${ctx.subTheme}";
  estimatedPages: 2;
  verbs: Array<{
    infinitive: string;
    translation: string;
    tense: string;
    conjugations: Record<string, string>;
    isIrregular: boolean;
  }>;
  exercises: Array<{
    sentence: string;
    hint: string;
    answer: string;
    verb: string;
  }>;
}
\`\`\`

Respond with ONLY the JSON object.${buildProgressionClause(ctx)}${buildExampleClause(ctx, 'conjugation_spotlight')}${buildCrossMonthClause(ctx)}${buildPreviousVerbsClause(ctx)}`;
}
